import * as React from 'react';
import { Check, Undo2 } from 'lucide-react';
import { isHoldExerciseLabel } from '../methods/bodybuildingFitness';

/**
 * IGYG "knock-down the reps" page — the second swipe page shown inside the host
 * (IgygHostPanel) and guest (PartnerStartedCard) IGYG views. Instead of tapping
 * −1 / +1 toward a target, each partner taps a big chunk block (5/10/15/20) that
 * knocks that many reps off the shared *remaining* total (e.g. 80 → 70). Taps are
 * clamped so the combined count never overshoots the target; a small Undo reverses
 * the last chunk. Rep mutations flow through the caller's `onKnockDown` (which wraps
 * the existing conflict-free addRep + partner-channel broadcast), so both partners
 * stay in sync — see lib/partnerSession.ts.
 */

const CHUNKS = [5, 10, 15, 20] as const;

// Fractions of the *target*, not of what's left: two 1/2 taps finish an exercise,
// four 1/4 taps do too. Fractions of the remainder would keep halving and never
// reach zero. Handy for splitting a big set down the middle with your partner.
const FRACTIONS = [
  { label: '½', divisor: 2 },
  { label: '¼', divisor: 4 },
  { label: '⅛', divisor: 8 },
] as const;

type Props = {
  exercises: { id: string; name: string; target: number | null }[];
  /** Combined reps done so far for an exercise (self + partner). */
  combinedFor: (exId: string) => number;
  /** Apply a signed rep delta for the current user (positive = knock down). */
  onKnockDown: (exId: string, delta: number) => void;
  /** Dim + ignore taps (e.g. guest before the host has started). */
  disabled?: boolean;
  t: (key: string, fallback?: string) => string;
};

export default function IgygRepBlocksPage({ exercises, combinedFor, onKnockDown, disabled, t }: Props) {
  // Per-exercise stack of the chunk sizes actually applied, so Undo reverses the
  // last real knock-down (which may be smaller than the tapped value if it was
  // clamped to the remaining reps).
  const [undoStacks, setUndoStacks] = React.useState<Record<string, number[]>>({});

  const knockDown = (exId: string, chunk: number, remaining: number | null) => {
    if (disabled) return;
    // With a target, never overshoot; without one, apply the full chunk (count up).
    const applied = remaining == null ? chunk : Math.min(chunk, remaining);
    if (applied <= 0) return;
    setUndoStacks((prev) => ({ ...prev, [exId]: [...(prev[exId] || []), applied] }));
    onKnockDown(exId, applied);
  };

  const undo = (exId: string) => {
    if (disabled) return;
    setUndoStacks((prev) => {
      const stack = prev[exId] || [];
      if (stack.length === 0) return prev;
      const last = stack[stack.length - 1]!;
      onKnockDown(exId, -last);
      return { ...prev, [exId]: stack.slice(0, -1) };
    });
  };

  return (
    <div className="flex flex-col gap-2 px-4 pb-4 pt-3 min-h-full">
      <div className="text-center text-[10px] font-semibold uppercase tracking-widest opacity-40">
        {t('Knock down reps', 'Knock down reps')}
      </div>

      {exercises.map((e) => {
        const combined = combinedFor(e.id);
        // Holds are timed: the chunk blocks add seconds, not reps.
        const isHold = isHoldExerciseLabel(e.name);
        const hasTarget = typeof e.target === 'number' && e.target > 0;
        const remaining = hasTarget ? Math.max(0, (e.target as number) - combined) : null;
        const done = hasTarget && remaining === 0;
        const canUndo = (undoStacks[e.id]?.length ?? 0) > 0;

        return (
          <div key={e.id} className="rounded-2xl border border-gray-200/60 dark:border-white/10 p-4">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="font-medium text-sm truncate">{e.name}</div>
              {hasTarget && (
                <div className="text-xs opacity-50 tabular-nums shrink-0">
                  {combined} / {e.target}{isHold ? ' s' : ''}
                </div>
              )}
            </div>

            {/* Hero: remaining (or combined when there's no target) */}
            <div className="text-center mb-3">
              {done ? (
                <div className="inline-flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
                  <Check size={28} />
                  <span className="text-2xl font-black">{t('Done', 'Done')}</span>
                </div>
              ) : (
                <>
                  <div className="text-6xl font-black tabular-nums leading-none">
                    {remaining != null ? remaining : combined}
                  </div>
                  <div className="text-[11px] uppercase tracking-widest opacity-40 mt-1.5">
                    {remaining != null
                      ? t('remaining', 'remaining')
                      : isHold
                        ? t('sec', 'sec')
                        : t('reps', 'reps')}
                  </div>
                </>
              )}
            </div>

            {/* Big chunk blocks */}
            <div className="grid grid-cols-4 gap-2">
              {CHUNKS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => knockDown(e.id, c, remaining)}
                  disabled={disabled || done}
                  aria-label={`${t('Knock down', 'Knock down')} ${c}${isHold ? ' s' : ''}`}
                  className="h-16 rounded-xl bg-brand-500 text-white text-2xl font-black grid place-items-center active:scale-95 transition disabled:opacity-30 disabled:active:scale-100"
                >
                  {c}
                </button>
              ))}
            </div>

            {/* Fractions of the target — only meaningful when there is one */}
            {hasTarget && (
              <div className="grid grid-cols-3 gap-2 mt-2">
                {FRACTIONS.map((f) => {
                  // Round up so the tap count never exceeds the divisor: 25 reps
                  // is 7+7+7+4, not 6+6+6+6 leaving a stray rep behind.
                  const amount = Math.max(1, Math.ceil((e.target as number) / f.divisor));
                  return (
                    <button
                      key={f.divisor}
                      type="button"
                      onClick={() => knockDown(e.id, amount, remaining)}
                      disabled={disabled || done}
                      aria-label={`${t('Knock down', 'Knock down')} ${f.label} (${amount})`}
                      className="h-16 rounded-xl bg-brand-500 text-white grid place-items-center leading-none active:scale-95 transition disabled:opacity-30 disabled:active:scale-100"
                    >
                      <span className="text-2xl font-black">{f.label}</span>
                      <span className="text-[11px] font-semibold tabular-nums opacity-70 mt-0.5">{amount}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Undo the last knock-down */}
            <div className="flex justify-end mt-2">
              <button
                type="button"
                onClick={() => undo(e.id)}
                disabled={disabled || !canUndo}
                className="inline-flex items-center gap-1.5 text-xs font-medium opacity-60 hover:opacity-100 disabled:opacity-25 disabled:hover:opacity-25 transition"
              >
                <Undo2 size={14} /> {t('Undo', 'Undo')}
              </button>
            </div>
          </div>
        );
      })}

      {exercises.length === 0 && (
        <div className="text-sm opacity-60 px-3 py-6 text-center">{t('No exercises in this block.', 'No exercises in this block.')}</div>
      )}
    </div>
  );
}
